<template>
  <section :class="'slide-align-' + align + ' text-white  text-' + textcolor" ref="sectionRef"
    class="introslide min-h-screen bg-workbook-dark-grey flex relative overflow-hidden" :style="image
      ? {
        backgroundImage: `url('${bgImageUrl}')`,
        backgroundSize: 'cover',
        backgroundPosition: '80% 50%'
      }
      : {}
      ">
    <div v-if="index > 0"
      class="opacity-0 lg:opacity-100 mybg absolute bottom-0  lg:top-0 left-0 w-full lg:w-1/2 h-1/2 lg:h-full bg-[#98614E] mix-blend-multiply z-0">

    </div>
    <UContainer class="flex z-20 relative md:items-center lg:flex-1 pt-20 md:pt-0" :class="{ 'items-end pb-20 md:pt-0': index > 0 }">
      <div class="flex gap-4 flex-col lg:flex-row" :class="{ 'lg:flex-row-reverse': align === 'right' }">
        <div class="w-full lg:w-1/2 lg:pr-24  lg:text-inherit">
          <slot />
          <!--TwicImg class="mobileimg" :src="props.image + '?deployment=' + config.public.appVersion" ratio="1/1"
              intrinsic="1716x1038" refit="5p" mode="cover" placeholder="none" /-->
        </div>

      </div>
    </UContainer>
  </section>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed } from 'vue'

import { useIntroStore } from '@/store/intro'
import { useWindowSize } from '@vueuse/core'
import { toTwicpicBgUrl, toTwicpicBgUrlMobile } from '@/utils/productImages'
import { loadGsap } from '@/utils/gsap'

import { useRuntimeConfig } from '#app'


const index = ref(0)
const config = useRuntimeConfig()

const introStore = useIntroStore()
const { width } = useWindowSize()

const sectionRef = ref<HTMLElement | null>(null)
const isMobile = computed(() => width.value < 767)

const props = defineProps({
  align: {
    type: String,
    default: 'left'
  },
  textcolor: {
    type: String,
    default: 'white'
  },
  image: {
    type: String
  },
  icons: {
    type: String
  }
})


const bgImageUrl = computed(() => {
  
  if (props.image) {
    if (isMobile.value && index.value > 0) {
      return toTwicpicBgUrlMobile(props.image + '?deployment=' + config.public.appVersion, false)
    } else {
      return toTwicpicBgUrl(props.image + '?deployment=' + config.public.appVersion, false)
    }
  }
  return ''
})

let ctx: gsap.Context | null = null


onMounted(async () => {
  index.value = introStore.mountedSlides.length
  
  if (sectionRef.value) {
    introStore.registerSlide(sectionRef.value)
  }


  if (process.client) {
    const { gsap } = await loadGsap()


    ctx = gsap.context(() => {
      const h1 = sectionRef.value?.querySelector('h1')
      const h2 = sectionRef.value?.querySelector('h2')
      const pElements = sectionRef.value?.querySelectorAll('p') || []
      const bg = sectionRef.value?.querySelector('.mybg');

      const scrollStart = computed(() => (index.value === 0 ? "top -10%" : "top 30%"))
      const scrollEnd = computed(() => "bottom 50%")
      const xStart = computed(() => (props.align === 'left' ? -100 : 100))
      const xEnd = computed(() => (props.align === 'left' ? 100 : -100))

      let tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.value,
          pin: index.value === 99,
          markers: false,
          start: scrollStart.value,
          end: scrollEnd.value,
          scrub: 2,
          snap: {
            snapTo: 'labels',
            duration: { min: 0.2, max: 3 },
            delay: 1.2,
          }
        }
      })

      // ANIMATION FOR FIRST SLIDE
      if (index.value === 0 && h1 && h2) {
        tl.to(h1, { x: 100, duration: 10 })
        tl.to(h1, { opacity: 0, duration: 2 }, "-=8")
        tl.to(h2, { x: 150, duration: 8 }, "-=8")
        tl.to(h2, { opacity: 0, duration: 2 }, "-=2")
        tl.to(pElements, { y: 100, duration: 6 }, "-=5")
        tl.to(pElements, { opacity: 0, duration: 2, stagger: 1 }, "-=2")
      }
      // ANIMATION FOR OTHER SLIDES
      if (index.value !== 0) {


        if (h1 && h2 && pElements && bg) {
          tl.fromTo(bg, { x: props.align === 'left' ? '-20%' : '0%', opacity: 0 }, { x: props.align === 'left' ? '0%' : '-20%', opacity: 1, duration: 10 }, 0)
          tl.from(h1, { x: xStart.value, duration: 10 }, "-=8")
          tl.from(h1, { opacity: 0, duration: 3 }, "-=10")


          tl.from(h2, { x: xStart.value, duration: 10 }, "-=8")
          tl.from(h2, { opacity: 0, duration: 3 }, "-=10")

          tl.from(pElements, { x: xStart.value, duration: 10 }, "-=8")
          tl.from(pElements, { opacity: 0, duration: 6, stagger: 1 }, "-=10")


          tl.addLabel("midpoint")

          tl.to(h1, { x: xEnd.value, duration: 10 }, "+=8")
          tl.to(h1, { opacity: 0, duration: 2 }, "-=6")

          tl.to(h2, { x: xEnd.value, duration: 10 }, "-=7")
          tl.to(h2, { opacity: 0, duration: 2 }, "-=6")

          tl.to(pElements, { y: 100, duration: 10 }, "-=7")
          tl.to(pElements, { opacity: 0, duration: 4, stagger: -1 }, "-=6")


          //tl.to(bg, {  opacity: 0, duration: 10 }, "-=8")

        } else {
          console.log('Missing elements in slide at index', index.value)
        }

      }
    }, sectionRef.value || undefined)
  }
})

onUnmounted(() => {
  ctx?.revert()
  ctx = null
})
</script>

<style scoped>
@reference "../../assets/css/main.css";

:deep(h1) {
  @apply text-base lg:text-2xl mb-4
}

:deep(h2) {
  @apply text-3xl lg:text-7xl mb-2 lg:mb-12
}

:deep(p) {
  @apply text-base lg:text-2xl
}

.slide-align-right :deep(h1) {
  @apply self-end
}

.slide-align-right :deep(h2) {
  @apply self-end
}

.slide-align-right :deep(p) {
  @apply self-end
}
</style>
